import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { defaultReducers } from './defaultReducers';
import type { Location } from './locationSlice';
import type { Skill } from './skillSlice';
import axios from 'axios';
import { scheduleSliceActions } from './scheduleSlice';
import { participantSliceActions } from './participantSlice';

export interface Coach {
  id: number;
  firstName: string;
  lastName: string;
}

export interface ScheduleCoach {
  id: number;
  user: Coach;
  status: string;
  note: string;
  locationStart: Location;
  locationEnd: Location;
  skill: Skill;
  scheduledParticipants: number[];
  transportationType: number;
}

export interface CoachState {
  loading: boolean;
  error: Error;
  list: number[];
  byId: { number: ScheduleCoach };
  searchStatus: string;
}

const initialState: CoachState = {
  loading: false,
  error: null,
  list: [],
  byId: {},
  searchStatus: '',
};

const coaches = createSlice({
  name: 'coaches',
  initialState,
  reducers: {
    getStart: defaultReducers.getStart,
    getSuccess: defaultReducers.getSuccess,
    getFailure: defaultReducers.getFailure,
    normalizeList: defaultReducers.normalizeList,
    updateCoach(state: CoachState, action: PayloadAction<any>) {
      state.loading = false;
      state.error = '';

      const scheduledCoach = state.byId[action.payload.id];
      if (!scheduledCoach) {
        return;
      }
      // Preserve participant list unless the payload explicitly includes it
      // (live coach deltas must not wipe who is on the coach).
      const preservedParticipants = scheduledCoach.scheduledParticipants;
      Object.assign(scheduledCoach, action.payload);
      if (
        action.payload.scheduledParticipants === undefined &&
        preservedParticipants
      ) {
        scheduledCoach.scheduledParticipants = preservedParticipants;
      }
      // Normalize location objects for AutoComplete when only ids arrive
      if (
        action.payload.locationStartId != null &&
        !action.payload.locationStart &&
        scheduledCoach.locationStart &&
        scheduledCoach.locationStart.id !== action.payload.locationStartId
      ) {
        // keep existing object if id matches; otherwise leave for locationStart payload
      }
      if (action.payload.locationStart) {
        scheduledCoach.locationStart = action.payload.locationStart;
      }
      if (action.payload.locationEnd) {
        scheduledCoach.locationEnd = action.payload.locationEnd;
      }
      if (action.payload.locationStartId === null) {
        scheduledCoach.locationStart = null;
      }
      if (action.payload.locationEndId === null) {
        scheduledCoach.locationEnd = null;
      }
    },
    addParticipantCoach(state: CoachState, action: PayloadAction<any>) {
      state.loading = false;
      state.error = '';

      const scheduledCoach = state.byId[action.payload.coachId];

      if (scheduledCoach) {
        const incoming = String(action.payload.participantId);
        const already = (scheduledCoach.scheduledParticipants || []).some(
          (participantId) => String(participantId) === incoming
        );
        if (already) {
          return;
        }
        scheduledCoach.scheduledParticipants.splice(
          0,
          0,
          action.payload.participantId
        );
      }
    },
    removeParticipantCoach(state: CoachState, action: PayloadAction<any>) {
      state.loading = false;
      state.error = '';

      const scheduledCoach = state.byId[action.payload.coachId];
      if (scheduledCoach && scheduledCoach.scheduledParticipants) {
        const idx = scheduledCoach.scheduledParticipants.findIndex(
          (participantId) => participantId === action.payload.participantId
        );
        // Only splice when found — findIndex -1 would remove the last item
        if (idx !== -1) {
          scheduledCoach.scheduledParticipants.splice(idx, 1);
        }
      }
    },
    removeReleasedParticipants(state: CoachState, action: PayloadAction<any>) {
      const { coachScheduleId, releasedIds } = action.payload;
      const coach = state.byId[coachScheduleId];
      if (coach) {
        coach.scheduledParticipants = coach.scheduledParticipants.filter(
          (id) => !releasedIds.includes(id)
        );
      }
    },
  },
});

export const {
  getStart,
  getSuccess,
  getFailure,
  normalizeList,
  updateCoach,
  addParticipantCoach,
  removeParticipantCoach,
  removeReleasedParticipants,
} = coaches.actions;

export const coachSliceActions = coaches.actions;
export const coachesReducer = coaches.reducer;

export const setCoachStatus = (searchStatus: string) => (dispatch: any) => {
  dispatch(getSuccess({ searchStatus }));
};

export const patchCoach = (coach: ScheduleCoach, isMaster: boolean) => async (
  dispatch: any
) => {
  try {
    // Suppress self-echo briefly so location change doesn't double-apply
    try {
      const { noteLocalParticipantMutation } = require('./participantSlice');
      noteLocalParticipantMutation();
    } catch (e) {
      // ignore
    }
    coach.isMaster = isMaster;
    const updatedCoach = await axios.patch(
      `${process.env.REACT_APP_SERVER_URL || ''}/schedules/scheduled_coach/${
        coach.id
      }/`,
      coach
    );
    dispatch(
      scheduleSliceActions.patchMeta({
        modifiedDate: updatedCoach.data.scheduleModifiedDate,
        isOutdated: false,
      })
    );
    // Merge server coach; updateCoach preserves scheduledParticipants
    dispatch(updateCoach(updatedCoach.data));
  } catch (err) {
    dispatch(getFailure(err));
  }
};

export const releaseCoachParticipants = (coachScheduleId: number) => async (
  dispatch: any
) => {
  try {
    const response = await axios.post(
      `${
        process.env.REACT_APP_SERVER_URL || ''
      }/schedules/scheduled_coach/${coachScheduleId}/release_participants/`
    );

    const participants = response.data.participants;
    participants.forEach((participant) => {
      dispatch(participantSliceActions.updateParticipant(participant));
    });

    const releasedIds = participants.map((p) => p.id);
    dispatch(removeReleasedParticipants({ coachScheduleId, releasedIds }));

    return response.data;
  } catch (err) {
    throw err;
  }
};

export const getCoachesReport = async (date_from: String, date_to: String) => {
  const report = await axios.get(
    `${
      process.env.REACT_APP_SERVER_URL || ''
    }/aerostar/reports/coaches_by_day?date_from=${date_from}&date_to=${date_to}`
  );
  return report.data;
};

export const getCoachCalendar = async (coach, date_from, date_to) => {
  try {
    const coachCalendar = await axios.get(
      `${
        process.env.REACT_APP_SERVER_URL || ''
      }/schedules/coach_calendar/${coach}/${date_from}/${date_to}/`
    );
    // Backend wraps the response as { events, synced_dates } — surface to
    // callers as { events, syncedDates } (the rest of the slice uses camelCase).
    const data = coachCalendar.data || {};
    return {
      events: data.events || [],
      syncedDates: data.synced_dates || [],
    };
  } catch (error) {
    console.log(error);
    return { events: [], syncedDates: [] };
  }
};
